/**
 * Customize Drawer
 * Handles opening/closing of the drawer and variant selection management
 */

class CustomizeDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-customize-drawer]');
    this.openButtons = document.querySelectorAll('[data-customize-open]');
    this.closeElements = document.querySelectorAll('[data-customize-close]');
    this.body = document.body;

    if (!this.drawer) return;

    // Get variant data
    const variantDataElement = document.querySelector('[data-drawer-variant-json]');
    this.variants = variantDataElement ? JSON.parse(variantDataElement.textContent) : [];

    // Current selection state
    this.selectedOptions = {};

    // Flag to prevent infinite loops when syncing
    this.isSyncing = false;

    // Track which button opened the drawer (for focus management)
    this.lastOpenButton = null;

    // Get option buttons
    this.optionButtons = this.drawer.querySelectorAll('[data-option-button]');

    // Get add to cart button and footer
    this.addToCartButton = this.drawer.querySelector('[data-drawer-add-to-cart]');
    this.footer = this.drawer.querySelector('.customize-drawer__footer');

    // Get Made to Order button
    this.madeToOrderButton = this.drawer.querySelector('[data-drawer-made-to-order-btn]');

    // Get price elements
    this.priceElement = this.drawer.querySelector('[data-drawer-price]');
    this.comparePriceElement = this.drawer.querySelector('[data-drawer-compare-price]');

    this.init();
  }

  init() {
    // Open drawer - handle both clicks and keyboard
    this.openButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.lastOpenButton = button;
        this.open();
      });

      // Handle keyboard navigation (Enter and Space)
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.lastOpenButton = button;
          this.open();
        }
      });
    });

    // Handle sticky cart "Change" button
    const stickyChangeButton = document.querySelector('[data-change-option-trigger]');
    if (stickyChangeButton) {
      stickyChangeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.lastOpenButton = stickyChangeButton;
        this.open();
      });
    }

    // Close drawer
    this.closeElements.forEach(element => {
      element.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });

    // Prevent clicks inside panel from closing
    const panel = this.drawer.querySelector('.customize-drawer__panel');
    if (panel) {
      panel.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Initialize selected options from current state
    this.initializeSelectedOptions();

    // Handle option button clicks
    this.optionButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.handleOptionClick(button);
      });
    });

    // Handle add to cart - make both button and footer clickable
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    if (this.footer) {
      this.footer.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    // Handle Made to Order button click
    if (this.madeToOrderButton) {
      this.madeToOrderButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // console.log('🔔 Drawer: Made to Order button clicked');
        this.handleMadeToOrder();
      });
    }

    // Update stock status for all options
    this.updateAllStockStatuses();

    // Listen to theme's variant change event to sync drawer with main variant selectors
    document.addEventListener('product:variant-change', (e) => {
      // Don't sync if we triggered this event ourselves
      if (!this.isSyncing && e.detail && e.detail.variant) {
        // console.log('🔄 Drawer: Received product:variant-change event', e.detail.variant);
        this.syncWithMainVariant(e.detail.variant);
      }
    });

    // CRITICAL: Listen to option selectors directly (Purity, Colour, Size)
    // These are the dropdowns/buttons users actually interact with
    const optionSelectors = document.querySelectorAll('select[data-index^="option"]');
    // console.log(`🎯 Drawer: Found ${optionSelectors.length} option selectors`);

    if (optionSelectors.length > 0) {
      optionSelectors.forEach((optionSelect, index) => {
        optionSelect.addEventListener('change', () => {
          // Don't sync if we triggered this change
          if (this.isSyncing) {
            // console.log(`🔄 Drawer: Option selector ${index + 1} changed but isSyncing=true, skipping`);
            return;
          }

          const optionName = optionSelect.name;
          const optionValue = optionSelect.value;
          // console.log(`🔄 Drawer: Option selector changed - ${optionName}: "${optionValue}"`);

          // Small delay to let the theme calculate the new variant
          setTimeout(() => {
            // Get all current option values
            const selectedOptions = {};
            optionSelectors.forEach((select) => {
              const index = select.getAttribute('data-index').replace('option', '');
              selectedOptions[index] = select.value;
            });

            // console.log('🔍 Drawer: Current option selections:', selectedOptions);

            // Find matching variant
            const matchingVariant = this.variants.find(variant => {
              return Object.keys(selectedOptions).every(position => {
                const optionKey = `option${position}`;
                return variant[optionKey] === selectedOptions[position];
              });
            });

            if (matchingVariant) {
              // console.log(`✅ Drawer: Found matching variant: ${matchingVariant.id} (${matchingVariant.title})`);
              // console.log(`📊 Drawer: Variant available? ${matchingVariant.available}`);

              // Skip if this is the same variant we just synced
              if (matchingVariant.id.toString() === this.lastSyncedVariantId) {
                // console.log('⏭️ Drawer: Same variant as before, skipping sync');
                return;
              }

              this.syncWithMainVariant(matchingVariant);
            } else {
              // console.warn('❌ Drawer: No matching variant found for options:', selectedOptions);
              // Dispatch event with null variant
              // console.log('📢 Drawer: Dispatching event with null variant');
              const event = new CustomEvent('product:variant-change', {
                detail: { variant: null },
                bubbles: true
              });
              document.dispatchEvent(event);
            }
          }, 50); // Small delay to ensure theme has processed the change
        });
      });

      // console.log('✓ Drawer: Added listeners to all option selectors');
    }

    // ALSO listen to option buttons (chips) for options like Size
    const optionButtons = document.querySelectorAll('[data-product-option] [data-button]');
    // console.log(`🎯 Drawer: Found ${optionButtons.length} option buttons (chips)`);

    if (optionButtons.length > 0) {
      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          // Don't sync if we triggered this change
          if (this.isSyncing) {
            // console.log('🔄 Drawer: Option button clicked but isSyncing=true, skipping');
            return;
          }

          const optionValue = button.getAttribute('data-option-value');
          // console.log(`🔄 Drawer: Option button clicked: "${optionValue}"`);

          // Small delay to let the theme calculate the new variant
          setTimeout(() => {
            // Get all current option values
            const selectedOptions = {};
            optionSelectors.forEach((select) => {
              const index = select.getAttribute('data-index').replace('option', '');
              selectedOptions[index] = select.value;
            });

            // console.log('🔍 Drawer: Current option selections after button click:', selectedOptions);

            // Find matching variant
            const matchingVariant = this.variants.find(variant => {
              return Object.keys(selectedOptions).every(position => {
                const optionKey = `option${position}`;
                return variant[optionKey] === selectedOptions[position];
              });
            });

            if (matchingVariant) {
              // console.log(`✅ Drawer: Found matching variant: ${matchingVariant.id} (${matchingVariant.title})`);
              // console.log(`📊 Drawer: Variant available? ${matchingVariant.available}`);

              // Skip if this is the same variant we just synced
              if (matchingVariant.id.toString() === this.lastSyncedVariantId) {
                // console.log('⏭️ Drawer: Same variant as before, skipping sync');
                return;
              }

              this.syncWithMainVariant(matchingVariant);
            } else {
              // console.warn('❌ Drawer: No matching variant found for options:', selectedOptions);
              // Dispatch event with null variant
              // console.log('📢 Drawer: Dispatching event with null variant');
              const event = new CustomEvent('product:variant-change', {
                detail: { variant: null },
                bubbles: true
              });
              document.dispatchEvent(event);
            }
          }, 50); // Small delay to ensure theme has processed the change
        });
      });

      // console.log('✓ Drawer: Added listeners to all option buttons');
    }

    // BACKUP LISTENER: Monitor the variant select directly
    // This catches changes that don't go through option selectors
    const variantSelect = document.querySelector('select[data-variant-select]');
    if (variantSelect) {
      // Store the last synced variant ID to avoid duplicate syncs
      this.lastSyncedVariantId = variantSelect.value;

      const syncFromVariantSelect = () => {
        // Don't sync if we triggered this change
        if (this.isSyncing) {
          // console.log('🔄 Drawer: Variant select changed but isSyncing=true, skipping');
          return;
        }

        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (!selectedOption) return;

        const variantId = selectedOption.value;

        // Skip if this is the same variant we just synced
        if (variantId === this.lastSyncedVariantId) {
          return;
        }

        // console.log('🔄 Drawer: Variant select changed to:', variantId, selectedOption.textContent);
        this.lastSyncedVariantId = variantId;

        // Find the variant object
        const variant = this.variants.find(v => v.id.toString() === variantId);
        if (variant) {
          // console.log('🔄 Drawer: Found variant, syncing drawer...', variant);
          this.syncWithMainVariant(variant);
        } else {
          // console.warn('🔄 Drawer: Could not find variant for ID:', variantId);
        }
      };

      // Listen to change events
      variantSelect.addEventListener('change', syncFromVariantSelect);

      // BACKUP: Use MutationObserver to catch programmatic changes
      // that might not trigger 'change' events
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            syncFromVariantSelect();
          }
        });
      });

      observer.observe(variantSelect, {
        attributes: true,
        attributeFilter: ['value'],
        childList: true,
        subtree: true
      });

      // console.log('✓ Drawer: Added listeners to variant select (change + mutation observer)');
    } else {
      // console.warn('⚠️ Drawer: Variant select not found');
    }
  }

  /**
   * Initialize selected options from currently selected variant
   */
  initializeSelectedOptions() {
    // First, populate selectedOptions from the drawer's initial state
    this.optionButtons.forEach(button => {
      if (button.classList.contains('customize-drawer__option-button--selected')) {
        const position = button.getAttribute('data-option-position');
        const value = button.getAttribute('data-option-value');
        this.selectedOptions[position] = value;
      }
    });

    // Then, initialize lastSyncedVariantId from the current variant
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.lastSyncedVariantId = currentVariant.id.toString();
      // console.log('🎬 Drawer: Initialized with variant:', currentVariant.id, currentVariant.title);
    }
  }

  /**
   * Handle option button click
   */
  handleOptionClick(button) {
    const position = button.getAttribute('data-option-position');
    const value = button.getAttribute('data-option-value');

    // Update selection state
    this.selectedOptions[position] = value;

    // Update UI - remove selected class from siblings
    const grid = button.closest('[data-option-grid]');
    if (grid) {
      grid.querySelectorAll('[data-option-button]').forEach(btn => {
        btn.classList.remove('customize-drawer__option-button--selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }

    // Add selected class to clicked button
    button.classList.add('customize-drawer__option-button--selected');
    button.setAttribute('aria-pressed', 'true');

    // Update stock statuses and price
    this.updateAllStockStatuses();
    this.updatePrice();
    this.updateCardDisplay();

    // Update last synced variant ID to prevent duplicate syncs
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.lastSyncedVariantId = currentVariant.id.toString();
      // Update sticky cart variant options
      this.updateStickyCartOptions(currentVariant);
    }

    // Sync with main variant selectors immediately
    this.isSyncing = true;
    this.updateMainVariantSelectors();

    // Reset flag after a short delay to allow events to propagate
    setTimeout(() => {
      this.isSyncing = false;

      // Manually dispatch product:variant-change event for Made to Order dialog
      // This happens when drawer changes and updates main selectors
      if (currentVariant) {
        // console.log('📢 Drawer: Dispatching product:variant-change after drawer change');
        const event = new CustomEvent('product:variant-change', {
          detail: { variant: currentVariant },
          bubbles: true
        });
        document.dispatchEvent(event);
      }
    }, 200);
  }

  /**
   * Update stock status for all option buttons
   */
  updateAllStockStatuses() {
    this.optionButtons.forEach(button => {
      const position = button.getAttribute('data-option-position');
      const value = button.getAttribute('data-option-value');

      // Check if this option combination has available variants
      const isAvailable = this.checkVariantAvailability(position, value);

      const stockStatusElement = button.querySelector('[data-stock-status]');
      if (stockStatusElement) {
        if (isAvailable) {
          stockStatusElement.textContent = 'In Stock';
          button.classList.remove('customize-drawer__option-button--out-of-stock');
        } else {
          stockStatusElement.textContent = 'Made to Order';
          button.classList.add('customize-drawer__option-button--out-of-stock');
        }
      }
    });

    // Update the add to cart button state
    this.updateAddToCartButton();
  }

  /**
   * Check if a variant is available for the given option
   */
  checkVariantAvailability(checkPosition, checkValue) {
    // Build a test selection with the option we're checking
    const testSelection = { ...this.selectedOptions, [checkPosition]: checkValue };

    // Find variants that match this selection
    const matchingVariants = this.variants.filter(variant => {
      // Check if all selected options match
      for (const [position, value] of Object.entries(testSelection)) {
        const optionKey = `option${position}`;
        if (variant[optionKey] !== value) {
          return false;
        }
      }
      return true;
    });

    // Available means at least one matching variant is in stock (not Made to Order)
    return matchingVariants.some(variant => !this.isVariantMadeToOrder(variant));
  }

  /**
   * Update add to cart button state based on variant availability
   * Also handles Made to Order button visibility
   */
  updateAddToCartButton() {
    const currentVariant = this.getCurrentVariant();

    if (!this.addToCartButton || !this.footer) return;

    if (!currentVariant) {
      // No variant selected - disable button, hide MTO button
      this.addToCartButton.textContent = 'Select All Options';
      this.addToCartButton.disabled = true;
      this.addToCartButton.style.display = 'block';
      this.footer.style.opacity = '0.6';
      this.footer.style.cursor = 'not-allowed';
      this.footer.style.pointerEvents = 'none';

      // Hide Made to Order button
      if (this.madeToOrderButton) {
        this.madeToOrderButton.style.display = 'none';
      }
    } else if (this.isVariantMadeToOrder(currentVariant)) {
      // Variant exists but out of stock - hide Add to Cart, show Made to Order
      this.addToCartButton.style.display = 'none';
      this.footer.style.opacity = '1';
      this.footer.style.cursor = 'pointer';
      this.footer.style.pointerEvents = 'auto';

      // Show Made to Order button
      if (this.madeToOrderButton) {
        this.madeToOrderButton.style.display = 'block';
        // Update button data attributes for MTO dialog
        this.madeToOrderButton.setAttribute('data-variant-id', currentVariant.id);
        this.madeToOrderButton.setAttribute('data-variant-title', currentVariant.title);
        this.madeToOrderButton.setAttribute('data-variant-price', currentVariant.price);
        // console.log('✨ Drawer: Made to Order button shown for sold-out variant:', currentVariant.title);
      }
    } else {
      // Variant available - show Add to Cart, hide Made to Order
      this.addToCartButton.textContent = 'Add To Cart';
      this.addToCartButton.disabled = false;
      this.addToCartButton.style.display = 'block';
      this.footer.style.opacity = '1';
      this.footer.style.cursor = 'pointer';
      this.footer.style.pointerEvents = 'auto';

      // Hide Made to Order button
      if (this.madeToOrderButton) {
        this.madeToOrderButton.style.display = 'none';
      }
    }
  }

  /**
   * Whether a variant should be treated as Made to Order.
   * True when there is no variant, when it is unavailable (inventory policy
   * "deny" + out of stock), or when inventory is tracked and quantity is 0 or
   * less (inventory policy "continue selling" — variant.available stays true at
   * 0 stock, so we must check the quantity directly).
   */
  isVariantMadeToOrder(variant) {
    if (!variant) return true;
    if (variant.available === false) return true;
    return variant.inventory_management != null && variant.inventory_quantity <= 0;
  }

  /**
   * Get currently selected variant
   */
  getCurrentVariant() {
    return this.variants.find(variant => {
      return Object.keys(this.selectedOptions).every(position => {
        const optionKey = `option${position}`;
        return variant[optionKey] === this.selectedOptions[position];
      });
    });
  }

  /**
   * Update price display
   */
  updatePrice() {
    const currentVariant = this.getCurrentVariant();
    if (currentVariant && this.priceElement) {
      // Format price using Shopify's money formatting if available
      const price = currentVariant.price / 100;
      const formattedPrice = this.formatMoney(price);
      this.priceElement.textContent = formattedPrice;

      // Update compare-at price if it exists
      if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
        const comparePrice = currentVariant.compare_at_price / 100;

        // If compare price element doesn't exist, create it
        if (!this.comparePriceElement) {
          const priceWrapper = this.drawer.querySelector('.customize-drawer__price-wrapper');
          if (priceWrapper) {
            this.comparePriceElement = document.createElement('span');
            this.comparePriceElement.className = 'customize-drawer__compare-price';
            this.comparePriceElement.setAttribute('data-drawer-compare-price', '');
            priceWrapper.appendChild(this.comparePriceElement);
          }
        }

        if (this.comparePriceElement) {
          const formattedComparePrice = this.formatMoney(comparePrice);
          this.comparePriceElement.textContent = formattedComparePrice;
          this.comparePriceElement.style.display = 'inline';
        }
      } else {
        // Hide compare price if not applicable
        if (this.comparePriceElement) {
          this.comparePriceElement.style.display = 'none';
        }
      }
    }

    // Update add to cart button state
    this.updateAddToCartButton();
  }

  /**
   * Format money with currency symbol
   * Uses Shopify's theme settings if available, otherwise defaults to Rs.
   */
  formatMoney(amount) {
    // Try to use Shopify's money format from theme settings
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(amount * 100, Shopify.money_format || 'Rs. {{amount}}');
    }

    // Fallback to manual formatting with Rs.
    const formattedAmount = amount.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return `Rs. ${formattedAmount}`;
  }

  /**
   * Update the customize card display with new selections
   */
  updateCardDisplay() {
    Object.keys(this.selectedOptions).forEach(position => {
      const value = this.selectedOptions[position];
      const cardValueElement = document.querySelector(`[data-customize-value="${position}"]`);
      if (cardValueElement) {
        cardValueElement.textContent = value;
      }
    });
  }

  /**
   * Update sticky cart variant options display
   */
  updateStickyCartOptions(variant) {
    if (!variant) return;

    const stickyOptionsElement = document.querySelector('.sticky-atc-bar__meta-options');
    if (stickyOptionsElement && variant.options) {
      const optionValueString = variant.options.join(", ");
      stickyOptionsElement.textContent = optionValueString;
    }
  }

  /**
   * Sync drawer selection with main variant (when main selectors change)
   */
  syncWithMainVariant(variant) {
    // console.log('📥 Drawer: syncWithMainVariant called with variant:', variant.id, variant.title);

    // Update last synced variant ID to prevent duplicate syncs
    this.lastSyncedVariantId = variant.id.toString();

    // Update selected options from variant
    for (let i = 1; i <= 3; i++) {
      const optionValue = variant[`option${i}`];
      if (optionValue) {
        this.selectedOptions[i.toString()] = optionValue;

        // Update drawer button states
        const grid = this.drawer.querySelector(`[data-option-grid="${i}"]`);
        if (grid) {
          grid.querySelectorAll('[data-option-button]').forEach(btn => {
            const btnValue = btn.getAttribute('data-option-value');
            if (btnValue === optionValue) {
              btn.classList.add('customize-drawer__option-button--selected');
              btn.setAttribute('aria-pressed', 'true');
            } else {
              btn.classList.remove('customize-drawer__option-button--selected');
              btn.setAttribute('aria-pressed', 'false');
            }
          });
        }
      }
    }

    // Update price and stock statuses
    this.updatePrice();
    this.updateAllStockStatuses();

    // Manually dispatch product:variant-change event for Made to Order dialog
    // console.log('📢 Drawer: Manually dispatching product:variant-change event');
    const event = new CustomEvent('product:variant-change', {
      detail: { variant: variant },
      bubbles: true
    });
    document.dispatchEvent(event);

    // console.log('✅ Drawer: Sync complete. Selected options now:', this.selectedOptions);
  }

  /**
   * Update the main product page variant selectors
   */
  updateMainVariantSelectors() {
    Object.keys(this.selectedOptions).forEach(position => {
      const value = this.selectedOptions[position];

      // 1. Update the select dropdown (hidden or visible)
      const variantSelect = document.querySelector(`select[data-index="option${position}"]`);
      if (variantSelect && variantSelect.value !== value) {
        variantSelect.value = value;

        // Trigger change event with bubbling
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        variantSelect.dispatchEvent(changeEvent);
      }

      // 2. Update any visible swatch/chip UI elements
      const optionWrapper = document.querySelector(`[data-product-option="${position}"]`);
      if (optionWrapper) {
        // Handle color swatches
        const swatches = optionWrapper.querySelectorAll('[data-swatch]');
        swatches.forEach(swatch => {
          const swatchValue = swatch.getAttribute('data-value');
          if (swatchValue === value) {
            swatch.click();
          }
        });

        // Handle chip buttons
        const chips = optionWrapper.querySelectorAll('.product__chip');
        chips.forEach(chip => {
          const chipInput = chip.querySelector('input[type="radio"]');
          if (chipInput && chipInput.value === value) {
            chipInput.checked = true;
            chipInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Handle regular radio buttons
        const radioButtons = optionWrapper.querySelectorAll('input[type="radio"]');
        radioButtons.forEach(radio => {
          if (radio.value === value) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });

    // DIRECT TRIGGER: Update price breakup after drawer changes variant
    // Calculate the variant ID ourselves based on selected options
    setTimeout(() => {
      if (!window.priceBreakupSync) return;

      // Find the variant that matches the selected options
      const matchingVariant = this.getCurrentVariant();

      if (matchingVariant) {
        // console.log('🎯 Drawer: Selected options are:', this.selectedOptions);
        // console.log('🎯 Drawer: Found matching variant ID:', matchingVariant.id);
        // console.log('🎯 Drawer: Variant title:', matchingVariant.title);
        // console.log('🎯 Drawer: Directly triggering price breakup update...');

        // Disable option selector listeners temporarily to prevent conflict
        if (window.priceBreakupSync.disableOptionSelectorsTemporarily) {
          window.priceBreakupSync.disableOptionSelectorsTemporarily();
        }

        window.priceBreakupSync.updateDisplay(matchingVariant.id);
      } else {
        // console.warn('⚠️ Drawer: No matching variant found for options:', this.selectedOptions);

        // Fallback: use variant selector value
        const variantSelect = document.querySelector('select[data-variant-select]');
        if (variantSelect) {
          // console.log('🎯 Drawer: Fallback to variant selector:', variantSelect.value);
          window.priceBreakupSync.updateDisplay(variantSelect.value);
        }
      }
    }, 100); // Reduced delay since we're calculating ourselves
  }

  /**
   * Handle add to cart button click
   */
  handleAddToCart() {
    const currentVariant = this.getCurrentVariant();

    if (!currentVariant) {
      alert('Please select all options');
      return;
    }

    // Made to Order items should go through the MTO flow, not straight to cart
    if (this.isVariantMadeToOrder(currentVariant)) {
      alert('This variant is currently sold out');
      return;
    }

    // Close drawer first for better UX
    this.close();

    // Update the main product variant selectors
    // This will trigger the theme's natural variant change flow
    this.isSyncing = true;
    this.updateMainVariantSelectors();

    // Wait for variant updates to complete, then add to cart
    setTimeout(() => {
      this.isSyncing = false;
      const productForm = document.querySelector('[data-product-form]');
      if (productForm) {
        const addToCartButton = productForm.querySelector('[name="add"]');
        if (addToCartButton && !addToCartButton.disabled) {
          addToCartButton.click();
        }
      }
    }, 300);
  }

  /**
   * Handle Made to Order button click
   */
  handleMadeToOrder() {
    const currentVariant = this.getCurrentVariant();

    if (!currentVariant) {
      alert('Please select all options');
      return;
    }

    // console.log('📦 Drawer: Made to Order clicked for variant:', currentVariant);

    // Get product name
    const productNameEl = document.querySelector('.product__title') ||
                         document.querySelector('[data-product-title]') ||
                         document.querySelector('h1');
    const productName = productNameEl ? productNameEl.textContent.trim() : 'Product';

    // Build variant details object
    const variantDetails = {
      productName: productName,
      variantId: currentVariant.id,
      variantTitle: currentVariant.title,
      variantPrice: currentVariant.price,
      productUrl: window.location.href
    };

    // Check if the Made to Order dialog exists
    if (window.madeToOrderDialog && typeof window.madeToOrderDialog.open === 'function') {
      // console.log('✅ Drawer: Opening Made to Order dialog');
      window.madeToOrderDialog.open(variantDetails);

      // Close drawer after a short delay
      setTimeout(() => {
        this.close();
      }, 100);
    } else {
      // Fallback: construct WhatsApp URL directly
      // console.warn('⚠️ Drawer: Made to Order dialog not found, using fallback');
      this.openWhatsAppDirectly(variantDetails);
      this.close();
    }
  }

  /**
   * Fallback: Open WhatsApp directly if dialog doesn't exist
   */
  openWhatsAppDirectly(details) {
    const whatsappNumber = '916360580124'; // From made-to-order-dialog.js config
    const depositPercentage = 50;
    const storeName = 'Reia Diamonds';

    // Calculate deposit
    const depositAmount = (details.variantPrice * depositPercentage) / 100;

    // Format prices
    const formatPrice = (price) => {
      const priceInRupees = price / 100;
      const formatted = priceInRupees.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return `Rs. ${formatted}`;
    };

    // Remove metal weight from variant title
    const cleanedVariantTitle = details.variantTitle.replace(/\s*\/?\s*\d+\.?\d*\s*(gm|g|gram|grams)\s*/gi, '').trim();

    // Build WhatsApp message
    const lines = [
      `Hello,`,
      `I would like to order a bespoke piece from ${storeName}.`,
      ``,
      `Product: ${details.productName}`,
      `Variant: ${cleanedVariantTitle}`,
      `Total Price: ${formatPrice(details.variantPrice)}`,
      ``,
      `I understand this is a made-to-order item and I am ready to pay ${depositPercentage}% advance payment of ${formatPrice(depositAmount)} to begin production.`,
      ``,
      `Product Link: ${details.productUrl}`,
      ``,
      `Could you please give me further details about:`,
      `- Production timeline`,
      `- Payment process for the advance`,
      `- Customization options (if any)`,
      `- Balance payment and shipping`,
      ``,
      `Thank You!`
    ];

    const message = lines.join('\n');
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    // console.log('📱 Drawer: Opening WhatsApp directly');
    window.open(whatsappUrl, '_blank');
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    this.body.classList.add('customize-drawer-open');

    // Update stock statuses when opening
    this.updateAllStockStatuses();
    this.updatePrice();

    // Focus trap - focus first focusable element
    setTimeout(() => {
      const closeButton = this.drawer.querySelector('.customize-drawer__close');
      if (closeButton) closeButton.focus();
    }, 100);
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.body.classList.remove('customize-drawer-open');

    // Return focus to the button that opened the drawer (if it still exists)
    if (this.lastOpenButton && document.body.contains(this.lastOpenButton)) {
      // Don't focus if it's the sticky cart button (to prevent scroll jump)
      const isStickyButton = this.lastOpenButton.hasAttribute('data-change-option-trigger');
      if (!isStickyButton) {
        this.lastOpenButton.focus();
      }
    }
  }
}

// Initialize when DOM is ready and expose to window
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.customizeDrawer = new CustomizeDrawer();
    // console.log('✅ Drawer: Instance exposed to window.customizeDrawer');
  });
} else {
  window.customizeDrawer = new CustomizeDrawer();
  // console.log('✅ Drawer: Instance exposed to window.customizeDrawer');
}
